import apiClient from '@/services/api-client';
import { authStorage } from './storage';

export interface Agent {
  id: string;
  username: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  createdAt: string;
}

export interface LoginResponse {
  success: boolean;
  agent: Agent;
  sessionToken: string;
  expiresAt: number;
}

export interface RegisterResponse {
  success: boolean;
  agent: Agent;
}

export const agentAuthAPI = {
  /**
   * Login an agent with username and password
   */
  async login(username: string, password: string): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<LoginResponse>('/agent/login', { username, password });
      // Response format: { success: true, data: { agent, sessionToken, expiresAt } }
      const responseData = response?.success === true ? response.data : response;
      return {
        success: responseData?.success !== false,
        agent: responseData?.agent || responseData,
        sessionToken: responseData?.sessionToken,
        expiresAt: responseData?.expiresAt
      };
    } catch (error) {
      console.error('Agent login error:', error);
      throw error;
    }
  },

  /**
   * Register a new agent
   */
  async register(username: string, password: string, name: string, email: string, phone: string): Promise<RegisterResponse> {
    try {
      const response = await apiClient.post<RegisterResponse>('/agent/register', { username, password, name, email, phone });
      // Response format: { success: true, data: { agent } }
      const responseData = response?.success === true ? response.data : response;
      return {
        success: responseData?.success !== false,
        agent: responseData?.agent || responseData
      };
    } catch (error) {
      console.error('Agent registration error:', error);
      throw error;
    }
  },

  /**
   * Verify current session and get agent info
   */
  async verifySession(sessionToken: string): Promise<{ success: boolean; agent: Agent }> {
    try {
      const response = await apiClient.get<{ success: boolean; agent: Agent }>('/agent/verify', {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });
      // Response format: { success: true, data: { agent } }
      const responseData = response?.success === true ? response.data : response;
      return {
        success: responseData?.success !== false,
        agent: responseData?.agent
      };
    } catch (error) {
      console.error('Session verification error:', error);
      throw error;
    }
  },

  /**
   * Logout current agent session
   */
  async logout(sessionToken: string): Promise<{ success: boolean }> {
    try {
      const response = await apiClient.post<{ success: boolean }>('/agent/logout', {}, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });
      // Response format: { success: true, data: { ... } }
      return {
        success: response?.success === true
      };
    } catch (error) {
      console.error('Agent logout error:', error);
      throw error;
    }
  },

  /**
   * Get all agents (admin only)
   */
  async getAllAgents(): Promise<{ agents: Agent[] }> {
    try {
      const response = await apiClient.get<{ agents: Agent[] }>('/agent/all');
      // Response format: { success: true, data: { agents: [...] } }
      const responseData = response?.success === true ? response.data : response;
      return {
        agents: responseData?.agents || []
      };
    } catch (error) {
      console.error('Error fetching agents:', error);
      throw error;
    }
  },

  /**
   * Seed test agents (development only)
   */
  async seedTestAgents(): Promise<{ success: boolean; message: string; agents: Array<{ username: string; password: string; name: string }> }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string; agents: Array<{ username: string; password: string; name: string }> }>('/agent/seed');
      // Response format: { success: true, data: { message, agents } }
      const responseData = response?.success === true ? response.data : response;
      return {
        success: responseData?.success !== false,
        message: responseData?.message || '',
        agents: responseData?.agents || []
      };
    } catch (error) {
      console.error('Error seeding test agents:', error);
      throw error;
    }
  }
};

/**
 * Local storage key for agent session token
 */
const AGENT_SESSION_KEY = 'agent_session_token';
const AGENT_INFO_KEY = 'agent_info';

export const agentSessionStorage = {
  saveSession(sessionToken: string, agent: Agent) {
    authStorage.set(AGENT_SESSION_KEY, sessionToken);
    authStorage.set(AGENT_INFO_KEY, agent);
  },

  getSessionToken(): string | null {
    return authStorage.get<string>(AGENT_SESSION_KEY);
  },

  getAgentInfo(): Agent | null {
    return authStorage.get<Agent>(AGENT_INFO_KEY);
  },

  clearSession() {
    authStorage.remove(AGENT_SESSION_KEY);
    authStorage.remove(AGENT_INFO_KEY);
  },

  hasSession(): boolean {
    return !!this.getSessionToken();
  }
};

/**
 * Bridge helper: populate agentSessionStorage from a Cognito User.
 * Call this after a successful Cognito login when the user has the 'agent' role,
 * so downstream components that read from agentSessionStorage continue to work.
 */
export function bridgeCognitoToAgentSession(user: {
  id: string;
  username: string;
  name: string;
  email: string;
}, cognitoToken: string): void {
  const agent: Agent = {
    id: user.id,
    username: user.username,
    name: user.name,
    email: user.email,
    phone: '',
    status: 'active',
    createdAt: new Date().toISOString(),
  };
  agentSessionStorage.saveSession(cognitoToken, agent);
}
